import React, { useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { ScanLine, Info } from "lucide-react";
import IntroVideo from "@/components/IntroVideo";

const money = (n) => "$" + Math.round(n).toLocaleString();

function Row({ label, value, sub, strong, color }) {
  return (
    <div className={`flex items-baseline justify-between py-3 ${strong ? "border-t-2 border-neutral-200" : "border-t border-[#E5E2DC]"}`}>
      <div>
        <p className={`${strong ? "font-head text-lg font-semibold text-neutral-900" : "text-neutral-700"}`}>{label}</p>
        {sub && <p className="text-sm text-neutral-500">{sub}</p>}
      </div>
      <p className={`font-head font-semibold ${strong ? "text-2xl" : "text-lg"}`} style={{ color: color || "#1C1B1A" }}>{value}</p>
    </div>
  );
}

export default function PnLDecoder() {
  const [revenue, setRevenue] = useState(120000);
  const [cogs, setCogs] = useState(48000);
  const [overhead, setOverhead] = useState(42000);
  const [saveState, setSaveState] = useState("");
  const edited = useRef(false);

  useEffect(() => {
    client.get("/me/pnl").then(({ data }) => {
      if (data.pnl && !edited.current) {
        setRevenue(data.pnl.revenue);
        setCogs(data.pnl.cogs);
        setOverhead(data.pnl.overhead);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!edited.current) return;
    setSaveState("Saving…");
    const t = setTimeout(() => {
      client.put("/me/pnl", { revenue, cogs, overhead })
        .then(() => setSaveState("Saved to your account"))
        .catch(() => setSaveState("Couldn't save — try again"));
    }, 800);
    return () => clearTimeout(t);
  }, [revenue, cogs, overhead]);

  const grossProfit = revenue - cogs;
  const grossMargin = revenue ? (grossProfit / revenue) * 100 : 0;
  const netProfit = grossProfit - overhead;
  const netMargin = revenue ? (netProfit / revenue) * 100 : 0;

  const plainEnglish = () => {
    if (!revenue) return "Enter your revenue to begin. Revenue is simply everything you brought in from sales.";
    const parts = [];
    parts.push(`For every $100 you sell, you keep about ${money(grossMargin)} after the direct cost of delivering it. That's your gross margin (${grossMargin.toFixed(1)}%).`.replace("$", ""));
    if (netProfit >= 0) parts.push(`After overhead, you keep ${netMargin.toFixed(1)}¢ of every dollar as profit — that's ${money(netProfit)} a year. `);
    else parts.push(`Right now overhead is eating into your gross profit — you're ${money(Math.abs(netProfit))} short. This is fixable, and your roadmap shows how.`);
    return parts.join(" ");
  };

  const field = "min-h-[48px] w-full rounded-xl border border-neutral-200 pl-8 pr-4 outline-none transition-all focus:border-[var(--tenant-primary)] focus:ring-2 focus:ring-[var(--tenant-primary-light)]";

  const inputs = [
    { label: "Revenue", help: "Everything you brought in from sales", val: revenue, set: setRevenue, testid: "revenue-input" },
    { label: "Cost of Goods Sold (COGS)", help: "Direct cost of making/delivering what you sell", val: cogs, set: setCogs, testid: "cogs-input" },
    { label: "Overhead", help: "Rent, salaries, software — your fixed costs", val: overhead, set: setOverhead, testid: "overhead-input" },
  ];

  return (
    <div className="fade-up space-y-8">
      <div className="flex items-start gap-4">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl" style={{ backgroundColor: "var(--tenant-primary-light)" }}><ScanLine className="tenant-text" strokeWidth={1.5} /></span>
        <div>
          <h1 className="font-head text-3xl font-semibold tracking-tight text-neutral-900">P&L Decoder</h1>
          <p className="mt-1 max-w-xl text-neutral-600">Type in your three numbers. We'll translate your Profit & Loss into plain English — no accounting degree required.</p>
        </div>
      </div>

      <IntroVideo
        testid="intro-video-pnl"
        title="Meet the P&L Decoder"
        subtitle="Watch Penny-the-Baker & Ollie-the-Wise-Owl decode a P&L in about a minute."
        embedUrl="https://dubb.com/v/2USiTt/embed?width=auto&height=auto&autoplay=0&no_cta=1&no_controls=0&muted=0"
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-[#E5E2DC] bg-white p-7">
          <h3 className="font-head text-lg font-medium text-neutral-900">Your Big Three (annual)</h3>
          <div className="mt-5 space-y-5">
            {inputs.map((f) => (
              <div key={f.label}>
                <label className="mb-1.5 block text-sm font-medium text-neutral-700">{f.label}</label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400">$</span>
                  <input data-testid={f.testid} type="number" min="0" value={f.val} onChange={(e) => { edited.current = true; f.set(Number(e.target.value) || 0); }} className={field} />
                </div>
                <p className="mt-1 text-xs text-neutral-400">{f.help}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs">
            <p className="text-neutral-500" data-testid="pnl-private-note">Private to you. Ballpark is fine.</p>
            {saveState && <p className="tenant-text font-medium" data-testid="pnl-save-status">{saveState}</p>}
          </div>
        </div>

        <div className="rounded-2xl border border-[#E5E2DC] bg-white p-7" data-testid="pnl-result">
          <h3 className="font-head text-lg font-medium text-neutral-900">Your P&L, decoded</h3>
          <div className="mt-4">
            <Row label="Revenue" value={money(revenue)} />
            <Row label="− Cost of Goods Sold" value={money(cogs)} color="#D96C4A" />
            <Row label="Gross Profit" sub={`Gross margin ${grossMargin.toFixed(1)}%`} value={money(grossProfit)} color="#7A9B76" strong />
            <Row label="− Overhead" value={money(overhead)} color="#D96C4A" />
            <Row label="Net Profit" sub={`Net margin ${netMargin.toFixed(1)}%`} value={money(netProfit)} color={netProfit >= 0 ? "var(--tenant-primary)" : "#D96C4A"} strong />
          </div>
          <div className="mt-6 flex gap-3 rounded-xl p-4" style={{ backgroundColor: "var(--tenant-primary-light)" }}>
            <Info size={18} className="tenant-text mt-0.5 shrink-0" />
            <p className="text-sm leading-relaxed text-neutral-700" data-testid="pnl-plain-english">{plainEnglish()}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
